/**
 * 保留中の勤怠データをローカルストレージで管理するユーティリティ
 *
 * データ形式:
 * {
 *   [date: string]: {
 *     registeredAt: number,  // 登録時刻（タイムスタンプ）
 *     kintaiData: object,    // 勤怠データ
 *     isOffice: boolean      // 出社フラグ
 *   }
 * }
 */

const STORAGE_KEY_PREFIX = "pendingKintai_";
const EXPIRY_HOURS = 1; // 1時間経過後に期限切れとみなす

/**
 * ユーザーごとのストレージキーを取得
 */
const getStorageKey = (userId) => `${STORAGE_KEY_PREFIX}${userId}`;

/**
 * 保留中の勤怠データを全て取得
 */
export const getPendingKintaiData = (userId) => {
  try {
    const key = getStorageKey(userId);
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error("ローカルストレージの読み込みエラー:", error);
    return {};
  }
};

/**
 * 保留中の勤怠データを保存
 */
export const savePendingKintai = (userId, date, kintaiData, isOffice) => {
  try {
    const key = getStorageKey(userId);
    const existingData = getPendingKintaiData(userId);

    existingData[date] = {
      registeredAt: Date.now(),
      kintaiData,
      isOffice,
    };

    localStorage.setItem(key, JSON.stringify(existingData));
    return true;
  } catch (error) {
    console.error("ローカルストレージの保存エラー:", error);
    return false;
  }
};

/**
 * 特定の日付の保留中データを削除
 */
export const removePendingKintai = (userId, date) => {
  try {
    const key = getStorageKey(userId);
    const existingData = getPendingKintaiData(userId);

    if (existingData[date]) {
      delete existingData[date];
      localStorage.setItem(key, JSON.stringify(existingData));
    }
    return true;
  } catch (error) {
    console.error("ローカルストレージの削除エラー:", error);
    return false;
  }
};

/**
 * サーバーから取得した勤怠データと照合し、登録済みのデータをローカルストレージから削除
 * 期限切れのデータも検出して返す
 *
 * @returns {Object} { expiredDates: string[] } - 期限切れの日付リスト
 */
export const cleanupPendingKintai = (userId, serverKintaiDates) => {
  try {
    const pendingData = getPendingKintaiData(userId);
    const now = Date.now();
    const expiryTime = EXPIRY_HOURS * 60 * 60 * 1000; // 1時間をミリ秒に変換
    const expiredDates = [];

    // サーバーに登録済みの日付セットを作成
    const registeredDatesSet = new Set(
      serverKintaiDates
        .filter(status => status.is_registered)
        .map(status => status.date)
    );

    Object.keys(pendingData).forEach(date => {
      const entry = pendingData[date];
      const isRegisteredOnServer = registeredDatesSet.has(date);
      const isExpired = (now - entry.registeredAt) > expiryTime;

      if (isRegisteredOnServer) {
        // サーバーに登録済み → ローカルストレージから削除
        delete pendingData[date];
      } else if (isExpired) {
        // 期限切れ（1h経過してもサーバーに登録されていない）→ 失敗として報告
        expiredDates.push(date);
        delete pendingData[date];
      }
    });

    // 更新後のデータを保存
    const key = getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(pendingData));

    return { expiredDates };
  } catch (error) {
    console.error("ローカルストレージのクリーンアップエラー:", error);
    return { expiredDates: [] };
  }
};

/**
 * 特定の日付が保留中かどうかを確認
 */
export const isPendingKintai = (userId, date) => {
  const pendingData = getPendingKintaiData(userId);
  return !!pendingData[date];
};

/**
 * 保留中の日付リストを取得
 */
export const getPendingDates = (userId) => {
  const pendingData = getPendingKintaiData(userId);
  return Object.keys(pendingData);
};
